import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import useListFilters from "../../hooks/useListFilters";
import {
  SP,
  deleteAdminUser,
  useAdminUsers,
  useMe,
} from "../../http/serviceportalApi";
import { useTranslations } from "../../hooks/useTranslations";
import { Permission } from "../../types";
import { getRemoteDataStatus } from "../../utils";
import SVGDelete from "../../icons/Delete.svg?react";
import ModalDialog from "../ui/ModalDialog";
import Page from "../ui/Page";
import Table from "../ui/Table";
import TableButton, { TableButtonCell } from "../ui/TableButton";
import styles from "./AdminUsers.module.css";

const AdminUsers = () => {
  const { i18n } = useTranslations();
  const filters = useListFilters({ isSearchable: true });
  const aborter = useRef(new AbortController());
  const {
    data: adminUsers,
    isValidating,
    error,
    mutate,
  } = useAdminUsers(filters.state, aborter.current.signal);
  const items = adminUsers?.items ?? [];
  const hasNextPage = adminUsers?.next_page !== undefined;
  const status = getRemoteDataStatus({ isValidating, error });

  const { data: me } = useMe();
  const canWrite = me && me.permissions.includes(Permission.UsersWrite);

  return (
    <Page title={i18n.admin.users.title}>
      <Table
        filters={filters}
        isTall={true}
        searchOptions={{
          placeholder: i18n.admin.users.searchPlaceholder,
          onChange: () => {
            aborter.current.abort();
            aborter.current = new AbortController();
          },
        }}
        head={
          <tr>
            <th align="left">{i18n.admin.users.columnHeaders.name}</th>
            {canWrite && <th></th>}
          </tr>
        }
        body={items.map((user, index) => {
          const displayName = user.name
            ? user.name
            : `${user.firstName} ${user.lastName}`;
          return (
            <tr key={index}>
              <td>
                <Link to={user.id}>{displayName}</Link>
                <span className={styles.email}>{user.email}</span>
              </td>
              {canWrite && (
                <TableButtonCell>
                  <DeleteUserButton
                    user={user}
                    onSubmit={() =>
                      deleteAdminUser(user.id).then(() => {
                        if (!adminUsers) return;
                        mutate();
                      })
                    }
                  />
                </TableButtonCell>
              )}
            </tr>
          );
        })}
        hasNextPage={hasNextPage}
        remoteDataStatus={status}
      />
    </Page>
  );
};

interface DeleteUserButtonProps {
  user: SP.User;
  companyName?: string;
  onSubmit: () => Promise<any>;
}

const DeleteUserButton = ({
  user: { email },
  onSubmit,
}: DeleteUserButtonProps) => {
  const { i18n } = useTranslations();
  const [isModalOpen, setModalOpen] = useState(false);
  const [isSubmitting, setSubmitting] = useState(false);

  return (
    <>
      <ModalDialog
        title={i18n.admin.users.deleteModal.title}
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        actions={[
          {
            title: i18n.admin.users.deleteModal.cancel,
            type: "secondary",
            onClick: () => setModalOpen(false),
          },
          {
            title: i18n.admin.users.deleteModal.confirm,
            type: "destructive",
            isSubmitting: isSubmitting,
            onClick: () => {
              setSubmitting(true);
              onSubmit()
                .then(() => setModalOpen(false))
                .catch(() => setSubmitting(false));
            },
          },
        ]}
      >
        <p>{i18n.admin.users.deleteModal.body(email)}</p>
      </ModalDialog>
      <TableButton glyph={SVGDelete} onClick={() => setModalOpen(true)} />
    </>
  );
};

export default AdminUsers;
